import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import {
  BonusTransactionDto,
  isBonusTransactionStatusActive,
  isBonusTransactionStatusClosed,
} from 'api/Transaction/Types/BonusTransactionDto'
import { TransactionBonusFilterKey } from 'api/Transaction/Types/BonusType'
import { ReduceBonusReserved } from 'api/Transaction/Types/ReduceBonusReserved'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { Pager } from 'components/components/Pager'
import { Dialog } from '@/components/ui/dialog'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import {
  SelectBonusCampaignFilter,
  SelectBonusCampaignModalOpenEvent,
} from 'components/Filter/BonusCampaignFields/SelectBonusCampaignFilter'
import { getBonusTransactionsFields } from 'components/Filter/bonusTransactionsFields'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from 'components/Filter/ClientFields/SelectClientFilter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { FilterCell } from 'components/Filter/FilterCell'
import {
  SelectWalletFilter,
  SelectWalletModalOpenEvent,
} from 'components/Filter/WalletFields/SelectWalletFilter'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  setGridFilters,
} from 'store/grid'
import {
  fetchBonusTransactionStatusesAction,
  fetchBonusTransactionTypeAction,
  fetchTransactionStatusAction,
} from 'store/transactions/transaction.action'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'
import { isTransactionCompletedStatus } from 'utils/transaction/status.index'

import { SelectClientEvent, SelectClientTable } from '../Records/Clients/SelectClientTable'
import SelectWalletTable, { SelectWalletEvent } from '../Records/Wallets/SelectWalletTable'
import bonusTransactionsColumn from './bonusTransactionsColumns'
import { ReduceBonusReservedFormModal } from './BonusTransactionsReservedModal'
import { SelectBonusCampaignEvent, SelectBonusCampaignTable } from './SelectBonusCampaignTable'
import TransactionReversePage from './TransactionReverse/TransactionReversePage'

const useMapStateToProps = () => ({
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const walletsApiClient = useApiClient(WalletsApiClient)

  return {
    fetchTransactionStatus: async () => dispatch(fetchTransactionStatusAction(walletsApiClient)),
    fetchTransactionBonusStatuses: async () =>
      dispatch(fetchBonusTransactionStatusesAction(walletsApiClient)),
    fetchTransactionBonusTypes: async () =>
      dispatch(fetchBonusTransactionTypeAction(walletsApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

const BonusTransactionsOverview = (): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const [auth] = useContext(AuthSessionContext)
  const setToast = useContext(ToastContext)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const crumbs = [{ title: 'Transactions - Bonus', link: links.transactionBonus.url }]
  const permissions = auth?.permissions || []

  const [transactions, setTransactions] = useState<BonusTransactionDto[]>([])
  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [isWalletModalOpen, setWalletModalOpen] = useState<boolean>(false)
  const [isBonusCampaignModalOpen, setBonusCampaignModalOpen] = useState<boolean>(false)
  const [isReduceBonusReservedModalOpen, setReduceBonusReservedModalOpen] = useState<boolean>(false)

  const [bonusCampaignModalFilterId, setBonusCampaignModalFilterId] = useState<string>()
  const [clientModalFilterId, setClientModalFilterId] = useState<string>()
  const [walletModalFilterId, setWalletModalFilterId] = useState<string>()
  const [bonusTransactionData, setBonusTransactionData] = useState<BonusTransactionDto | null>()

  const [reverseTransactionModal, setReverseTransactionModal] = useState<{
    data?: TransactionDto
    visible: boolean
  }>({ data: undefined, visible: false })

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { gridFilterByFilterIdSelector } = useMapStateToProps()
  const { fetchTransactionBonusTypes, fetchTransactionBonusStatuses, fetchTransactionStatus } =
    useMapDispatchToProps()

  const transactionState = useAppSelector((state) => state.transactions.status)
  const bonusTypes = useAppSelector((state) => state.transactions.bonusTypes)
  const bonusStatuses = useAppSelector((state) => state.transactions.bonusStatuses)

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<BonusTransactionDto[]>> => {
          const searchQuery = getSearchQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await walletsApiClient.getBonusTransactions({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(transactionState)) await fetchTransactionStatus()
          if (isEmpty(bonusTypes)) await fetchTransactionBonusTypes()
          if (isEmpty(bonusStatuses)) await fetchTransactionBonusStatuses()

          if (response?.items) {
            setTransactions(response.items)
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<BonusTransactionDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (query?: PageQuery): Promise<void> => {
      const searchQuery = query?.search !== undefined ? query.search : getSearchQuery()
      const sortQuery = query?.sort ?? getSort(gridState.sort)
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize

      const response = await walletsApiClient.getBonusTransactions({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setTransactions(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getSearchQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }

  const handleClientModalClose = () => {
    setClientModalOpen(false)
    setClientModalFilterId(undefined)
  }

  const handleBonusCampaignModalOpen = (event: SelectBonusCampaignModalOpenEvent) => {
    setBonusCampaignModalOpen(true)
    setBonusCampaignModalFilterId(event.filterId)
  }

  const handleBonusCampaignModalClose = () => {
    setBonusCampaignModalOpen(false)
    setBonusCampaignModalFilterId(undefined)
  }
  const handleWalletModalOpen = (event: SelectWalletModalOpenEvent) => {
    setWalletModalOpen(true)
    setWalletModalFilterId(event.filterId)
  }

  const handleWalletModalClose = () => {
    setWalletModalOpen(false)
    setWalletModalFilterId(undefined)
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getSearchQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await walletsApiClient.getBonusTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getBonusTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getBonusTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setTransactions(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await walletsApiClient.getBonusTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSelectedBonusCampaigns = (event: SelectBonusCampaignEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }
  const handleSelectedWallets = (event: SelectWalletEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleReduceBonusReserved = (bonusTransaction: BonusTransactionDto) => {
    setBonusTransactionData(bonusTransaction)
    setReduceBonusReservedModalOpen(true)
  }

  const handleReduceBonusReservedModalClose = () => {
    setBonusTransactionData(null)
    setReduceBonusReservedModalOpen(false)
  }

  const handleSubmitReduceBonusReserved = async (values: ReduceBonusReserved) => {
    if (bonusTransactionData?.id) {
      await walletsApiClient.reduceReservedTransactionBonuses(bonusTransactionData?.id, values)
      setBonusTransactionData(null)
      setReduceBonusReservedModalOpen(false)
      setToast(successToast('Reserved Bonuses for this transaction has been reduced.'))
      await filterFetch()
    }
  }

  const handleCancelBonus = async (bonusTransaction: BonusTransactionDto) => {
    await walletsApiClient.cancelTransactionBonuses(
      bonusTransaction.id,
      bonusTransaction.walletTransactionId
    )
    setToast(successToast('Bonuses for this transaction have been canceled.'))
    await filterFetch()
  }

  const handleReverseTransaction = (transaction: TransactionDto) => {
    setReverseTransactionModal({ data: transaction, visible: true })
  }

  const handleReverseCompleteTransactionClose = async () => {
    setReverseTransactionModal({ data: undefined, visible: false })
    await filterFetch()
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      {isBonusCampaignModalOpen && (
        <Modal
          width={900}
          visible={isBonusCampaignModalOpen}
          onClose={handleBonusCampaignModalClose}
        >
          <SelectBonusCampaignTable
            filterId={bonusCampaignModalFilterId}
            onSelect={handleSelectedBonusCampaigns}
            onClose={handleBonusCampaignModalClose}
          />
        </Modal>
      )}
      {isWalletModalOpen && (
        <Modal width={900} visible={isWalletModalOpen} onClose={handleWalletModalClose}>
          <SelectWalletTable
            filterId={walletModalFilterId}
            onSelect={handleSelectedWallets}
            onClose={handleWalletModalClose}
          />
        </Modal>
      )}
      {isReduceBonusReservedModalOpen && bonusTransactionData && (
        <Modal
          width={900}
          visible={isReduceBonusReservedModalOpen}
          onClose={handleReduceBonusReservedModalClose}
        >
          <ReduceBonusReservedFormModal
            bonusTransaction={bonusTransactionData}
            onSubmit={handleSubmitReduceBonusReserved}
          />
        </Modal>
      )}
      {reverseTransactionModal.visible && !!reverseTransactionModal.data && (
        <Modal
          width={600}
          padding='3rem'
          visible={reverseTransactionModal.visible}
          onClose={handleReverseCompleteTransactionClose}
        >
          <TransactionReversePage
            transaction={reverseTransactionModal.data}
            onClose={handleReverseCompleteTransactionClose}
          />
        </Modal>
      )}
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.BonusTransferOverview}
        filterFields={[
          {
            name: TransactionBonusFilterKey.client,
            label: 'Client',
            filter: (clientFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectClientFilter {...clientFilter} onClientModalOpen={handleClientModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: TransactionBonusFilterKey.wallet,
            label: 'Wallet',
            filter: (walletFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectWalletFilter {...walletFilter} onWalletModalOpen={handleWalletModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: TransactionBonusFilterKey.campaign,
            label: 'Campaign',
            filter: (campaignFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => {
              return (
                <SelectBonusCampaignFilter
                  {...campaignFilter}
                  onCampaignModalOpen={handleBonusCampaignModalOpen}
                />
              )
            },
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          ...getBonusTransactionsFields(),
        ]}
        containerClassName='wallet-table__filter'
        getQuery={getSearchQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport
          data={transactions}
          hasPermissions={hasPermission(permissions, PermissionsEnum.CanExportTransactionsModule)}
          columns={bonusTransactionsColumn}
        >
          <Grid
            {...gridState}
            resizable
            data={transactions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {bonusTransactionsColumn
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
            <Column
              width={50}
              title=''
              filterCell={FilterCell}
              cell={(gridProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Reduce Bonus Reserved',
                        'data-test': 'reduce-bonus-reserved',
                        disabled: !isBonusTransactionStatusActive(
                          gridProps.dataItem?.bonusTransactionStatus?.id
                        ),
                        onClick: () => handleReduceBonusReserved(gridProps.dataItem),
                      },
                      {
                        name: 'Cancel Bonus',
                        'data-test': 'cancel-bonus',
                        disabled: !isBonusTransactionStatusActive(
                          gridProps.dataItem?.bonusTransactionStatus?.id
                        ),
                        onClick: () => handleCancelBonus(gridProps.dataItem),
                      },
                      {
                        name: 'Reverse',
                        'data-test': 'reverse',
                        disabled: !(
                          isTransactionCompletedStatus(gridProps.dataItem.state.id) &&
                          isBonusTransactionStatusClosed(
                            gridProps.dataItem?.bonusTransactionStatus?.id
                          )
                        ),
                        onClick: () => handleReverseTransaction(gridProps.dataItem),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

export default BonusTransactionsOverview
